// #24 RatingScale: one radiogroup, 5 → 1, each tile a number in its ring colour (--mc-rc: green, blue, gold, orange,
// red) beside its descriptor from the pinned Interview Guide, then *Unassessed* as a real sixth radio (alt drew it
// as a plain box). One tab stop; arrows move and select. The VP+ lock is the blocked form: every tile aria-disabled
// and described by the reason, choosing does nothing. `mode="view"` shows the chosen value only.
// form="playbook" (DC-244, B14 C6): his playbook's "Graded score rating (select one)": one radiogroup named by the
// caller ("Rating for <Value>"), radios 1 → 5 ascending with the bare number visible, each radio's accessible name
// "<n> — <descriptor>" (the visible number starts the name) and the descriptor as its tooltip, neutral rings with the
// chosen radio in the selection blue, then the Unassessed radio last in the same group.

import type { ReactNode } from "react";
import { Tooltip } from "../lib/Tooltip";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useSvgId } from "../lib/useSvgId";
import { vars, type McVarValue } from "../lib/vars";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import type { Blocked } from "../core/Button";
import { useChoice, type ChoiceItem } from "./choice";
import { SCORECARD_LABELS } from "./labels";

export type ScoreValue = 1 | 2 | 3 | 4 | 5;
export type RatingValue = ScoreValue | "na";

export const RATING_SCALE: readonly ScoreValue[] = [5, 4, 3, 2, 1];
const VALUES: readonly RatingValue[] = [...RATING_SCALE, "na"];

/** The ring colour of each number (alt's RC): a fill token, used only as the ring and the chosen disc. */
export const RATING_RING: Readonly<Record<ScoreValue, McVarValue>> = {
  5: "var(--green)", 4: "var(--blue)", 3: "var(--gold)", 2: "var(--orange)", 1: "var(--red)",
};

export interface RatingScaleChipsProps {
  /** Today's 5 → 1 descriptor chips (default). */
  form?: "chips" | undefined;
  /** The group's name: the section rated (a ROOTS value, a skill). Default "Rating". */
  label?: string | undefined;
  value?: RatingValue | null | undefined;
  defaultValue?: RatingValue | null | undefined;
  onChange?: ((value: RatingValue) => void) | undefined;
  /** The guide's descriptor for each number (configuration content, pinned with the guide version). */
  descriptors?: Partial<Record<ScoreValue, ReactNode>> | undefined;
  can?: boolean | undefined;
  blocked?: Blocked | undefined;
  mode?: "edit" | "view" | undefined;
  hs?: Partial<Record<RatingValue, Hs>> | undefined;
  className?: string | undefined;
}

function Tile({ rating, descriptor, radio, checked, blocked, describedBy, hs }: {
  rating: RatingValue; descriptor?: ReactNode; radio: ChoiceItem; checked: boolean; blocked: boolean; describedBy: string | undefined; hs: Hs | undefined;
}) {
  const hot = useHs(hs);
  const na = rating === "na";
  return (
    <div
      {...radio}
      className={cx("mc-rate", na && "mc-na", checked && "mc-on", blocked && "mc-dis")}
      style={na ? undefined : vars({ "--mc-rc": RATING_RING[rating] })}
      aria-disabled={blocked ? true : undefined}
      aria-describedby={describedBy}
      {...hot}
    >
      {na ? null : <span className="mc-rn">{rating}</span>}
      <span className="mc-rtx">{na ? SCORECARD_LABELS.notAssessed : descriptor ?? null}</span>
    </div>
  );
}

/** The playbook form's descriptor keys ("1" … "5"), as the guide's anchors carry them. */
export type PlaybookDescriptors = Readonly<Record<"1" | "2" | "3" | "4" | "5", string>>;

export interface RatingScalePlaybookProps {
  form: "playbook";
  /** The group's accessible name: "Rating for <Value>" / "Rating for <Skill title>". */
  name: string;
  value: RatingValue | null;
  /** The pinned guide's descriptor of each number: each radio's name "<n> — <descriptor>" and its tooltip. */
  descriptors: PlaybookDescriptors;
  /** The last radio's word (the guide's not_assessed_label). */
  unassessedLabel: string;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  id?: string | undefined;
  onChange(value: RatingValue | null): void;
  className?: string | undefined;
}

export type RatingScaleProps = RatingScaleChipsProps | RatingScalePlaybookProps;

/** The playbook form's order: 1 → 5 ascending, then Unassessed. */
export const PLAYBOOK_RATING_ORDER: readonly RatingValue[] = [1, 2, 3, 4, 5, "na"];

/** "<n> — <descriptor>": the visible number starts the accessible name (WCAG 2.5.3); a number without one is bare. */
export function playbookRadioName(rating: ScoreValue, descriptor: string | undefined): string {
  return descriptor ? `${rating} — ${descriptor}` : String(rating);
}

function PlaybookRadio({ rating, radio, checked, disabled, name, tip, word }: {
  rating: RatingValue; radio: ChoiceItem; checked: boolean; disabled: boolean; name: string; tip: string | undefined; word: string;
}) {
  const na = rating === "na";
  const element = (
    <div
      {...radio}
      className={cx("mc-prate", na && "mc-na", checked && "mc-on", disabled && "mc-dis")}
      aria-label={na ? undefined : name}
      aria-disabled={disabled ? true : undefined}
    >
      <span className="mc-prate-d" aria-hidden="true" />
      <span className="mc-prate-n">{na ? word : rating}</span>
    </div>
  );
  return tip ? <Tooltip label={tip} mode="mirror">{element}</Tooltip> : element;
}

function PlaybookScale({ name, value, descriptors, unassessedLabel, disabled, invalid, id, onChange, className }: RatingScalePlaybookProps) {
  const choice = useChoice<RatingValue>({
    values: PLAYBOOK_RATING_ORDER, value, onChange: (next) => onChange(next), isBlocked: () => Boolean(disabled),
  });
  return (
    <div className="mc-prates-w">
      <div
        role="radiogroup"
        id={id}
        aria-label={name}
        aria-invalid={invalid ? true : undefined}
        aria-disabled={disabled ? true : undefined}
        className={cx("mc-prates", invalid && "mc-prates-err", className)}
        {...choice.container}
      >
        {PLAYBOOK_RATING_ORDER.map((rating, at) => {
          const descriptor = rating === "na" ? undefined : descriptors[String(rating) as keyof PlaybookDescriptors];
          return (
            <PlaybookRadio
              key={rating}
              rating={rating}
              radio={choice.item(at)}
              checked={at === choice.index}
              disabled={Boolean(disabled)}
              name={rating === "na" ? unassessedLabel : playbookRadioName(rating, descriptor)}
              tip={descriptor}
              word={unassessedLabel}
            />
          );
        })}
      </div>
    </div>
  );
}

export function RatingScale(props: RatingScaleProps) {
  if (props.form === "playbook") return <PlaybookScale {...props} />;
  return <ChipScale {...props} />;
}

function ChipScale({
  label = SCORECARD_LABELS.rating, value, defaultValue, onChange, descriptors, can, blocked, mode, hs, className,
}: RatingScaleChipsProps) {
  const id = useSvgId();
  const choice = useChoice<RatingValue>({ values: VALUES, value, defaultValue, onChange, isBlocked: () => blocked !== undefined });
  if (can === false) return null;
  if (mode === "view") {
    const shown = choice.chosen;
    return (
      <span className={cx("mc-rate-v", className)}>
        <VisuallyHidden>{label}</VisuallyHidden>
        {shown === null ? "—" : shown === "na" ? SCORECARD_LABELS.notAssessed : (
          <>
            <span className="mc-rn" style={vars({ "--mc-rc": RATING_RING[shown] })}>{shown}</span>
            {descriptors?.[shown] ?? null}
          </>
        )}
      </span>
    );
  }
  const why = `${id}-why`;
  const group = (
    <div role="radiogroup" aria-label={label} className={cx("mc-rates", className)} {...choice.container}>
      {VALUES.map((rating, at) => (
        <Tile
          key={rating}
          rating={rating}
          descriptor={rating === "na" ? undefined : descriptors?.[rating]}
          radio={choice.item(at)}
          checked={at === choice.index}
          blocked={blocked !== undefined}
          describedBy={blocked ? why : undefined}
          hs={hs?.[rating]}
        />
      ))}
    </div>
  );
  if (!blocked) return <div className="mc-rates-w">{group}</div>;
  return (
    <div className="mc-rates-w">
      <Tooltip label={blocked.reason} mode="mirror">{group}</Tooltip>
      <VisuallyHidden id={why}>{blocked.reason}</VisuallyHidden>
    </div>
  );
}
